import { useQuery } from "@tanstack/react-query";
import { getLeadMapping } from "../mappings";
import { useCallback, useEffect } from "react";
import {
  getJourneyByOldId,
  getLeadReferrals,
} from "@/modules/lead-page/api/lead-page.v2.api";
import { useLocation, useNavigate } from "react-router-dom";
import { queryCommonFlags } from "@/api/constants";
import { getStagesByJourney } from "@/modules/lead-page/api/lead-page.v2.api";
import { JourneyRelationModes } from "../types";

const useLeadPageDataFetching = (
  lead_id: string | undefined,
  redirect: boolean = true,
  mode: JourneyRelationModes = JourneyRelationModes.GET_LEAD,
  hasQRCode?: boolean
) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const isNanoId = /^\d+-[a-zA-Z0-9]{4}$/.test(lead_id || "");


  /**
   * §Canonicalize: the page was opened by something other than the nano id — a journey UUID, an old id — so
   * resolve it and rewrite the URL to the canonical form.
   *
   * `replace`, and the query string carried over. Both were missing, and together they broke every deep link
   * that arrives by uid:
   *
   *  - a PUSH made the redirect a place in history. Opening a board card cost TWO entries (measured: 8 -> 10),
   *    the first being the uid URL this hook immediately leaves — so Back landed the user back on the lead page
   *    they were trying to leave, looking like the button had done nothing but flicker. Back belongs to where
   *    the user came FROM; a canonical rewrite is not somewhere they were.
   *  - dropping `search` threw away `?tab=nawy_now`, which is the entire point of those links: the board sends
   *    you to the Nawy Now tab and you landed on Activity.
   */
  const getJourneyNanoId = useCallback(async () => {
    const { data } = await getJourneyByOldId({ id: lead_id });

    if (redirect) {
      navigate(`/leads-new/${data.nanoId}${search}`, { replace: true });
    }
  }, [lead_id, navigate, redirect, search]);

  useEffect(() => {
    if (!isNanoId && lead_id) {
      getJourneyNanoId();
    }
  }, [lead_id, isNanoId, getJourneyNanoId]);

  const {
    data: leadData,
    error: leadError,
    isError: leadIsError,
    isLoading,
  } = useQuery({
    queryKey: ["getLead", lead_id, mode, hasQRCode],
    enabled: !!lead_id && isNanoId,
    queryFn: () => getLeadMapping({ id: lead_id, mode, hasQRCode }),
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Prevent refetch on mount since data is shared via context
    refetchOnReconnect: true,
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchInterval: 600000, // 5 minutes
  });

  const { data: leadReferrals, isLoading: isReferralsLoading } = useQuery({
    queryKey: ["lead-referrals", leadData?.data?.lead.uid],
    queryFn: () => getLeadReferrals(leadData?.data?.lead.uid || ""),
    enabled: !!leadData?.data?.lead.uid,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  const {
    data: stagesData,
    isLoading: isStagesLoading,
    isFetching: isStagesFetching,
  } = useQuery({
    queryKey: ["getStagesByJourney", lead_id ?? "no-lead"],
    queryFn: () => {
      if (!lead_id) return Promise.resolve({ data: [] });
      return getStagesByJourney(lead_id);
    },
    ...queryCommonFlags,
    enabled: !!lead_id,
    refetchOnWindowFocus: false,
  });

  return {
    leadData,
    stagesData,
    leadReferrals,
    isLoading: isLoading || isReferralsLoading,
    isStagesLoading: isStagesLoading || isStagesFetching,
    leadError,
    leadIsError,
  };
};

export default useLeadPageDataFetching;
